import classNames from 'classnames'
import type { FC } from 'react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTabsContext } from './tabs-context'
import type { MoreTabProps, SelectionChangedEvent } from './types'
import { TAB_THEME_KEY } from './types'
import useForkRef from '../../hooks/use-fork-ref'
import useRefresh from '../../hooks/use-refresh'
import { useUniqueId } from '../../utils/unique-id'
import Box from '../_primitives/box/box'
import ToggleFlipIcon from '../_primitives/icon/toggle-flip-icon'
import Adorner from '../_utils/popover/adorner'
import Popover from '../_utils/popover/popover'
import type { PopoverOrigin, PopoverProps } from '../_utils/popover/types'
import { ButtonPrivate } from '../button/button'
import type { ButtonProps } from '../button/types'
import List from '../list/list'
import ListItem from '../list/list-item'

const responsiveSizes = ['xxs', 'xxs', 'xs', 'sm', 'sm']
const textProps = { fontSize: 'inherit' }

const MoreTab: FC<MoreTabProps> = props => {
  const {
    variant = 'tab',
    placeholder = 'More',
    children,
    includeValues,
    paneProps,
    buttonList,
    popoverProps,
    ...rest
  } = props
  const {
    variant: tabVariant,
    selectionChangedObserver,
    selection
  } = useTabsContext()
  const identifier = useUniqueId('csg-tui-more-tab')
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const paneRef = useRef<HTMLElement | null>(null)
  const item = includeValues.find(i => i.value === selection)
  const [selectTab, setSelectTab] = useState<
    | {
        label?: string
        icon?: ButtonProps['iconBefore']
      }
    | undefined
  >(
    item
      ? {
          label: item.label,
          icon: item.icon
        }
      : undefined
  )

  useEffect(() => {
    const onChangeHandler = (event: SelectionChangedEvent) => {
      const { value, label, icon } = event
      const item = includeValues.find(i => i.value === value)
      item ? setSelectTab({ label, icon }) : setSelectTab(undefined)
      setAnchorEl(null)
    }
    selectionChangedObserver?.attach(onChangeHandler)
    return () => selectionChangedObserver?.detach(onChangeHandler)
  }, [selectionChangedObserver, includeValues, setSelectTab])
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
    },
    []
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])
  const open = Boolean(anchorEl)
  const id = open ? identifier : undefined

  const isTop = (transformOrigin?: PopoverOrigin) =>
    transformOrigin?.vertical === 'bottom'

  const { paneProps: popoverPaneProps } = popoverProps || {
    children: null
  }

  const refreshCallback = useRefresh()

  const {
    // @ts-ignore
    ref: panePropsRef,
    ...restPaneProps
  } = { ...popoverPaneProps, ...paneProps }
  const handelPaneRef = useForkRef(paneRef, panePropsRef)
  const handleOnEntered = useCallback(
    (paneElement: HTMLElement) => {
      // @ts-ignore
      handelPaneRef(paneElement)
      refreshCallback()
    },
    [handelPaneRef, refreshCallback]
  )

  const listItems = (
    <List p="0" variant="buttonDropdownList" onClick={handleClose}>
      {React.Children.map(children, (g, i) => (
        <ListItem key={i} px="0">
          {g}
        </ListItem>
      ))}
    </List>
  )

  const adorner = useCallback(
    () =>
      buttonList ? (
        <Adorner
          variant={variant}
          anchorRect={anchorEl ? anchorEl.getBoundingClientRect() : null}
          dropdownRect={
            paneRef.current ? paneRef.current.getBoundingClientRect() : null
          }
          isTop={isTop(popoverProps?.transformOrigin)}
          borderShadowEclipseThickness={6}
        />
      ) : null,
    [anchorEl, buttonList, popoverProps?.transformOrigin, variant]
  )

  const iconAfter = useMemo(
    () => (
      <ToggleFlipIcon
        size={responsiveSizes}
        icon={buttonList ? 'mui-arrowDropDown' : 'chevronDown'}
        toggleState={!!anchorEl}
      />
    ),
    [anchorEl, buttonList]
  )

  const _popoverProps: Omit<PopoverProps, 'open'> = useMemo(
    () => ({
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'right'
      },
      paneProps: {
        variant: tabVariant ? `${tabVariant}.popover` : 'popover',
        __themeKey: TAB_THEME_KEY,
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
        borderRadius: 0,
        zIndex: 'selectPopover',
        ...paneProps,
        ...restPaneProps
      }
    }),
    [paneProps, restPaneProps, tabVariant]
  )

  return (
    <Box className="csg-tui-more-tab-wrapper" {...rest}>
      <ButtonPrivate
        as={buttonList ? undefined : 'a'}
        className={classNames({
          'csg-tui-tab-more': true,
          'is-selected': !!selectTab
        })}
        aria-describedby={id}
        variant={
          buttonList ? 'secondary' : tabVariant ? `${tabVariant}.tab` : variant
        }
        __themeKey={buttonList ? undefined : TAB_THEME_KEY}
        toggled={open}
        onClick={handleClick}
        height={buttonList ? '2em' : '100%'}
        iconBefore={selectTab?.icon}
        iconAfter={iconAfter}
        textProps={textProps}
      >
        {selectTab ? selectTab.label : placeholder}
      </ButtonPrivate>
      <Popover
        id={id}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        onEntered={buttonList ? handleOnEntered : undefined}
        disablePortal
        disableScrollLock
        adornerComponent={adorner}
        {..._popoverProps}
      >
        {buttonList ? listItems : (children as React.ReactElement)}
      </Popover>
    </Box>
  )
}

export default MoreTab
